import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';

import { toast } from 'sonner';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  CreditCard,
  LogOut,
  Menu,
  Plus,
  Search,
} from 'lucide-react';

import ContactDetail from './ContactDetail';
import AddContactDialog from './AddContactDialog';

export default function CRMDashboard({ setIsAuthenticated, user }) {
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Mobile-only UI state
  const [mobileView, setMobileView] = useState('list'); // "list" | "detail"
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      const data = response.data || [];
      setContacts(data);

      setSelectedContact((prev) => {
        if (prev) return prev;
        return data[0] || null;
      });
    } catch (error) {
      if (error.response?.status === 402) {
        navigate('/subscription-required');
      } else {
        toast.error('Failed to load contacts');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const handleContactCreated = (newContact) => {
    setContacts((prev) => [newContact, ...prev]);
    setSelectedContact(newContact);
    setShowAddDialog(false);
    setMobileView('detail');
  };

  const handleContactUpdated = (updatedContact) => {
    setContacts((prev) => prev.map((c) => (c.id === updatedContact.id ? updatedContact : c)));
    setSelectedContact(updatedContact);
  };

  const handleContactDeleted = (contactId) => {
    setContacts((prev) => {
      const next = prev.filter((c) => c.id !== contactId);
      setSelectedContact((sel) => {
        if (!sel || sel.id !== contactId) return sel;
        return next[0] || null;
      });
      return next;
    });

    setMobileView('list');
  };

  const openContactDetail = (contact) => {
    setSelectedContact(contact);
    setMobileView('detail');
  };

  const filteredContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return contacts;

    return contacts.filter((contact) => {
      const name = (contact.name || '').toLowerCase();
      const email = (contact.email || '').toLowerCase();
      const company = (contact.company || '').toLowerCase();
      return name.includes(q) || email.includes(q) || company.includes(q);
    });
  }, [contacts, searchQuery]);

  const getInitials = (name = '') =>
    name
      .split(' ')
      .filter(Boolean)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);

  const isTrialActive = () => {
    if (!user?.trial_end) return false;
    const trialEnd = new Date(user.trial_end);
    return new Date() < trialEnd;
  };

  const getDaysRemaining = () => {
    if (!user?.trial_end) return 0;
    const trialEnd = new Date(user.trial_end);
    const now = new Date();
    const diff = trialEnd - now;
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const SidebarContent = (
    <div className="h-full flex flex-col bg-white">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#14532D] flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-gray-900">Don&apos;t Forget</h1>
            <p className="text-xs text-gray-500 mt-0.5">Because follow-ups close deals.</p>
          </div>
        </div>
      </div>

      <div className="flex-1" />

      {user?.subscription_status === 'inactive' && isTrialActive() && (
        <div className="p-4 mx-4 mb-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-medium text-amber-900 mb-2">
            Trial: {getDaysRemaining()} days left
          </p>
          <Button
            size="sm"
            onClick={() => {
              setSidebarOpen(false);
              navigate('/subscription-required');
            }}
            className="w-full h-9 text-xs bg-[#1A4D2E] hover:bg-[#14532D]"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Upgrade / Support
          </Button>
        </div>
      )}

      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 border border-gray-100">
            <AvatarFallback className="bg-[#F0FDF4] text-[#1A4D2E] font-semibold">
              {getInitials(user?.name || 'User')}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email || ''}</p>
          </div>
        </div>

        <Button
          variant="outline"
          className="w-full mt-3 rounded-full"
          onClick={() => {
            setSidebarOpen(false);
            navigate('/support');
          }}
        >
          💚 Support this project
        </Button>

        <Button
          onClick={() => {
            setSidebarOpen(false);
            handleLogout();
          }}
          variant="ghost"
          className="w-full mt-3 justify-start text-gray-600 hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Logout
        </Button>
      </div>
    </div>
  );

  const ContactsList = (
    <div className="w-full lg:w-[400px] lg:border-r border-gray-200 bg-white/50 flex flex-col h-full">
      <div className="p-4 border-b border-gray-100 bg-white">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-gray-900">Contacts</h2>
          <Badge variant="secondary" className="bg-[#F0FDF4] text-[#1A4D2E] border-0">
            {contacts.length}
          </Badge>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search contacts..."
            className="pl-9 h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <Button
          onClick={() => setShowAddDialog(true)}
          className="w-full mt-3 h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white shadow-md hover:shadow-lg transition-all"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Contact
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2">
          {filteredContacts.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-500">
              {searchQuery ? (
                <>No contacts found.</>
              ) : (
                <>
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-[#1A4D2E]/10 flex items-center justify-center mb-3">
                    <CheckCircle2 className="w-6 h-6 text-[#1A4D2E]" />
                  </div>
                  <p className="font-medium text-gray-700">Add your first contact</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Start by adding someone you need to follow up with.
                  </p>
                  <Button
                    className="mt-4 bg-[#1A4D2E] hover:bg-[#14532D]"
                    onClick={() => setShowAddDialog(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </>
              )}
            </div>
          ) : (
            filteredContacts.map((contact) => {
              const isActive = selectedContact?.id === contact.id;

              return (
                <button
                  key={contact.id}
                  onClick={() => openContactDetail(contact)}
                  className={`w-full text-left rounded-xl border p-3 transition ${
                    isActive
                      ? 'border-[#1A4D2E]/40 bg-[#1A4D2E]/10'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-gradient-to-br from-[#1A4D2E] to-[#14532D] text-white font-semibold">
                        {getInitials(contact.name)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-900 truncate">{contact.name}</p>
                        {contact.status ? (
                          <Badge
                            variant="outline"
                            className={`text-[10px] ${
                              contact.status === 'active'
                                ? 'bg-green-50 text-green-700 border-green-200'
                                : 'bg-gray-50 text-gray-600 border-gray-200'
                            }`}
                          >
                            {contact.status}
                          </Badge>
                        ) : null}
                      </div>

                      {contact.company ? (
                        <p className="text-xs text-gray-500 truncate flex items-center gap-1 mt-0.5">
                          <Building2 className="w-3 h-3" />
                          {contact.company}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 truncate mt-0.5">—</p>
                      )}

                      {contact.email ? (
                        <p className="text-xs text-gray-400 truncate mt-1">{contact.email}</p>
                      ) : null}
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const DetailPanel = (
    <div className="flex-1 bg-white h-full overflow-y-auto">
      {selectedContact ? (
        <ContactDetail
          contact={selectedContact}
          onContactUpdated={handleContactUpdated}
          onContactDeleted={handleContactDeleted}
        />
      ) : (
        <div className="h-full flex items-center justify-center p-8 text-gray-400">
          <div className="text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium">Select a contact to view details</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen w-full flex overflow-hidden bg-[#F9F9F7] relative">
      <div className="noise-overlay" />

      {/* Desktop sidebar */}
      <div className="hidden lg:flex w-64 flex-shrink-0 border-r border-gray-200 bg-white">
        {SidebarContent}
      </div>

      {/* Mobile top bar */}
      <div className="lg:hidden absolute top-0 left-0 right-0 z-20 bg-white/90 backdrop-blur border-b border-gray-200">
        <div className="h-14 px-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] p-0">
                {SidebarContent}
              </SheetContent>
            </Sheet>

            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#1A4D2E] to-[#14532D] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-white" />
              </div>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-gray-900">Don&apos;t Forget</p>
                <p className="text-[11px] text-gray-500">Follow-ups</p>
              </div>
            </div>
          </div>

          {mobileView === 'detail' ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMobileView('list')}
              className="text-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : null}
        </div>
      </div>

      {/* Main content (mobile list/detail switch) */}
      <div className="flex-1 flex pt-14 lg:pt-0">
        <div className={`w-full lg:w-auto ${mobileView === 'detail' ? 'hidden lg:block' : 'block'}`}>
          {ContactsList}
        </div>

        <div className={`flex-1 ${mobileView === 'detail' ? 'block' : 'hidden lg:block'}`}>
          {DetailPanel}
        </div>
      </div>

      {showAddDialog && (
        <AddContactDialog
          open={showAddDialog}
          onClose={() => setShowAddDialog(false)}
          onContactCreated={handleContactCreated}
        />
      )}
    </div>
  );
}
